import kebabCase from 'lodash.kebabcase';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

if (!Object.entries) {
    Object.entries = function (obj) {
        var ownProps = Object.keys(obj);
        var i = ownProps.length;
        var resArray = new Array(i); // preallocate the Array
        while (i--) {
            resArray[i] = [ownProps[i], obj[ownProps[i]]];
        }
        return resArray;
    };
}

function generateBlockName(name) {
    return kebabCase(name.trim());
}
function generateElementName(block, name) {
    return block + "__" + kebabCase(name.trim());
}
function determineModifiers(block, elem, modifiers, conditions) {
    return Object.entries(__assign({}, modifiers, conditions)).reduce(function (mods, _a) {
        var mod = _a[0], active = _a[1];
        if (active) {
            // if mod is active
            mods.push(generateModifierName(block, elem, mod));
        }
        return mods;
    }, []);
}
function generateModifierName(block, elem, name) {
    var mod = kebabCase(name.trim());
    return !elem ? block + "--" + mod : elem + "--" + mod;
}
function addCssClass(el, className) {
    var classString = getElementClassString(el);
    if (!doesStringContainClass(classString, className)) {
        el.setAttribute('class', (classString + " " + className).trim());
    }
}
function addCssClasses(el, classNames) {
    classNames.forEach(function (c) { return addCssClass(el, c); });
}
function removeCssClass(el, className) {
    var classString = getElementClassString(el);
    if (doesStringContainClass(classString, className)) {
        // ensure new CSS class string is a space separated list
        var newClass = classString
            .replace(className, '')
            .split(/\s/)
            .join(' ');
        el.setAttribute('class', newClass);
    }
}
function removeCssClasses(el, classNames) {
    classNames.forEach(function (c) { return removeCssClass(el, c); });
}
function getElementClassString(el) {
    if (!el)
        return '';
    var classString = el.getAttribute('class');
    return !classString ? '' : classString;
}
function doesStringContainClass(classString, className) {
    if (!classString || !className)
        return false;
    return classString.indexOf(className) >= 0;
}
function generateBemClasses(binding, node) {
    var block = 'bem-block';
    var elem;
    var selfBlock = false;
    if (binding.arg === 'self' && node.componentOptions) {
        // if user wishes to add BEM inside of the child component context
        selfBlock = true; // mark this as true so an element won't be generated
        if (node.componentOptions.Ctor &&
            node.componentOptions.Ctor.extendOptions &&
            node.componentOptions.Ctor.extendOptions.name) {
            // if the child component name was found
            // typescript isn't happy about the extendOptions property
            block = generateBlockName(node.componentOptions.Ctor.extendOptions.name);
        }
        else if (node.componentOptions.tag) {
            // if the child component registered tag was found
            block = generateBlockName(node.componentOptions.tag);
        }
    }
    else if (node.context) {
        if (node.context.$options.name) {
            // if component name was given
            block = generateBlockName(node.context.$options.name);
        }
        else if (node.context.$vnode.componentOptions &&
            node.context.$vnode.componentOptions.tag) {
            // if component name wasn't given and component tag is available
            block = generateBlockName(node.context.$vnode.componentOptions.tag);
        }
    }
    if (!selfBlock && binding.arg) {
        elem = generateElementName(block, binding.arg);
    }
    var mods = determineModifiers(block, elem, binding.modifiers, binding.value);
    return [block, elem, mods];
}

var mutationConfig = {
    attributeFilter: ['class'],
    attributes: true
};
var observer = new MutationObserver(mutationCallback);
function hookUpMutationObserver(el) {
    observer.observe(el, mutationConfig);
}
function mutationCallback(mutationList) {
    mutationList.forEach(function (_a) {
        var type = _a.type, target = _a.target;
        if (type !== 'attributes')
            return;
        var _b = target, __$simpleBemBlock__ = _b.__$simpleBemBlock__, __$simpleBemElement__ = _b.__$simpleBemElement__, __$simpleBemMods__ = _b.__$simpleBemMods__;
        // ensure all of the proper CSS classes are present
        addCssClasses(target, [
            !__$simpleBemElement__ ? __$simpleBemBlock__ : __$simpleBemElement__
        ].concat(__$simpleBemMods__));
    });
}

var bemDirective = {
    inserted: function (el, binding, node) {
        var _a = generateBemClasses(binding, node), block = _a[0], elem = _a[1], mods = _a[2];
        var bemElement = el;
        addCssClasses(bemElement, [!elem ? block : elem].concat(mods));
        bemElement.__$simpleBemBlock__ = block;
        bemElement.__$simpleBemElement__ = elem;
        bemElement.__$simpleBemMods__ = mods;
        hookUpMutationObserver(bemElement);
    },
    update: function (el, binding, node) {
        if (JSON.stringify(binding.value) === JSON.stringify(binding.oldValue)) {
            // if the value hasn't changed then do nothing
            return;
        }
        var _a = generateBemClasses(binding, node), block = _a[0], elem = _a[1], mods = _a[2];
        var bemElement = el;
        bemElement.__$simpleBemBlock__ = block;
        bemElement.__$simpleBemElement__ = elem;
        bemElement.__$simpleBemMods__ = mods;
        var previousMods = determineModifiers(block, elem, binding.modifiers, binding.oldValue);
        var removedMods = previousMods.filter(function (m) { return mods.indexOf(m) < 0; });
        removeCssClasses(el, removedMods);
        addCssClasses(el, [!elem ? block : elem].concat(mods));
    }
};

var defaultOptions = {
    name: 'bem'
};
var bemPlugin = {
    install: function (vue, options) {
        var name = __assign({}, defaultOptions, options).name;
        vue.directive(name, bemDirective);
    }
};
var bem = bemDirective;

export { bemPlugin, bem };
